/**
 * Responsibility: open a retailer tab when stock is detected, and be honest
 * when the browser refuses.
 *
 * PLAN.md section 6.9. `window.open` from a WebSocket handler is not
 * user-initiated, so browsers block it by default; it works only once the
 * origin is allowlisted. This hook therefore always checks the return value -
 * `null` means blocked - and falls back to a Web Notification whose click
 * handler *is* trusted.
 *
 * Tabs are hard-capped per minute so a wave of eight SKUs does not open forty
 * tabs and bury the one that mattered.
 */
import { useCallback, useRef } from 'react';
import type { StockEvent } from '../api/client';

const DEFAULT_TABS_PER_MINUTE = 6;
const WINDOW_MS = 60_000;

export type AutoOpenOutcome = 'opened' | 'blocked' | 'rate_limited' | 'disabled';

export type AutoOpenResult = {
  attempt: (event: StockEvent) => AutoOpenOutcome;
  verify: () => Promise<boolean>;
  requestNotificationPermission: () => Promise<NotificationPermission>;
};

export function useAutoOpen(enabled: boolean, tabsPerMinute = DEFAULT_TABS_PER_MINUTE): AutoOpenResult {
  const openedAt = useRef<number[]>([]);

  const notify = useCallback((event: StockEvent, url: string) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const notification = new Notification(`In stock: ${event.retailer}`, {
      body: 'Tap to open the retailer page',
      tag: String(event.id),
    });
    // A click on a notification IS user-initiated, so this open is trusted
    // even though the automatic one was not.
    notification.onclick = () => window.open(url, '_blank', 'noopener');
  }, []);

  const attempt = useCallback(
    (event: StockEvent): AutoOpenOutcome => {
      const url = event.cart_url ?? event.product_url;
      if (!enabled) {
        notify(event, url);
        return 'disabled';
      }

      const now = Date.now();
      openedAt.current = openedAt.current.filter((t) => now - t < WINDOW_MS);
      if (openedAt.current.length >= tabsPerMinute) {
        notify(event, url);
        return 'rate_limited';
      }

      const opened = window.open(url, '_blank', 'noopener');
      if (opened === null) {
        // Blocked. Section 6.9 requires the fallback, not a silent failure.
        notify(event, url);
        return 'blocked';
      }
      openedAt.current.push(now);
      return 'opened';
    },
    [enabled, notify, tabsPerMinute],
  );

  const verify = useCallback(async (): Promise<boolean> => {
    // Fire from a timer so the call is genuinely not user-initiated - the same
    // conditions a real event faces. Verifying from a click would prove
    // nothing, which is the trap section 6.9 warns about.
    return new Promise<boolean>((resolve) => {
      window.setTimeout(() => {
        const probe = window.open('about:blank', '_blank', 'noopener,width=200,height=200');
        if (probe === null) {
          resolve(false);
          return;
        }
        probe.close();
        resolve(true);
      }, 250);
    });
  }, []);

  const requestNotificationPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied' as NotificationPermission;
    if (Notification.permission !== 'default') return Notification.permission;
    return Notification.requestPermission();
  }, []);

  return { attempt, verify, requestNotificationPermission };
}
